import { useRef, useState } from 'react'
import { classeStatut, codeSuivi, estFerme, LIBELLE_STATUT, TYPES } from '../lib/suivis'
import type { SuiviLocal, TypeSuivi } from '../lib/types'

const pct = (v: number) => `${v * 100}%`

/**
 * Épingles des suivis sur une page de plan. Chaque épingle porte le code
 * du suivi (D-004) ; un toucher ouvre la fiche. En mode « placer », un
 * toucher sur la page choisit l'endroit d'une nouvelle épingle.
 */
export function CoucheEpingles({
  suivis,
  placer,
  surbrillance,
  ouvrir,
  choisirPoint
}: {
  suivis: SuiviLocal[]
  placer: boolean
  surbrillance: string | null
  ouvrir: (id: string) => void
  choisirPoint: (x: number, y: number) => void
}) {
  const couche = useRef<HTMLDivElement>(null)
  const depart = useRef<{ x: number; y: number; id: number } | null>(null)
  const doigts = useRef(new Set<number>())
  const [brouillon, setBrouillon] = useState<[number, number] | null>(null)

  return (
    <div
      ref={couche}
      className={`epingles-couche${placer ? ' placer' : ''}`}
      onPointerDown={(e) => {
        if (!placer) return
        doigts.current.add(e.pointerId)
        // Deux doigts : on laisse le plan zoomer, ce n'est pas un toucher.
        if (doigts.current.size > 1) {
          depart.current = null
          return
        }
        if (e.pointerType === 'mouse' && e.button !== 0) return
        e.stopPropagation()
        depart.current = { x: e.clientX, y: e.clientY, id: e.pointerId }
      }}
      onPointerUp={(e) => {
        doigts.current.delete(e.pointerId)
        const d = depart.current
        depart.current = null
        if (!placer || !d || d.id !== e.pointerId || !couche.current) return
        e.stopPropagation()
        // Un glissement n'est pas un toucher (on déplaçait le plan).
        if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 10) return
        const r = couche.current.getBoundingClientRect()
        const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))
        const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))
        setBrouillon([x, y])
        choisirPoint(x, y)
      }}
      onPointerCancel={(e) => {
        doigts.current.delete(e.pointerId)
        depart.current = null
      }}
    >
      {suivis.map((s) => {
        const prefixe = TYPES.find((t) => t.cle === s.type)?.prefixe ?? ''
        return (
          <button
            key={s.id}
            className={`epingle t-${s.type}${estFerme(s) ? ' fermee' : ''}${surbrillance === s.id ? ' vive' : ''}`}
            style={{ left: pct(s.epingle_x ?? 0), top: pct(s.epingle_y ?? 0) }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              if (!placer) ouvrir(s.id)
            }}
            title={`${codeSuivi(s)} — ${s.titre} (${LIBELLE_STATUT[s.statut] ?? s.statut})`}
            aria-label={`${codeSuivi(s)} : ${s.titre}`}
          >
            <span className="epingle-tete">{prefixe}</span>
            <span className="epingle-code">{s.numero == null ? '…' : s.numero}</span>
          </button>
        )
      })}
      {placer && brouillon && (
        <span className="epingle brouillon" style={{ left: pct(brouillon[0]), top: pct(brouillon[1]) }}>
          <span className="epingle-tete">+</span>
        </span>
      )}
    </div>
  )
}

/**
 * Après avoir touché le plan en mode « placer » : créer un nouveau suivi à
 * cet endroit, ou y rattacher un suivi ouvert du chantier.
 */
export function DialogueEpingle({
  suivis,
  creer,
  rattacher,
  annuler
}: {
  suivis: SuiviLocal[]
  creer: (type: TypeSuivi) => void
  rattacher: (s: SuiviLocal) => void
  annuler: () => void
}) {
  const [filtre, setFiltre] = useState('')
  const ouverts = suivis
    .filter((s) => !estFerme(s))
    .filter((s) => {
      const q = filtre.trim().toLowerCase()
      return !q || `${codeSuivi(s)} ${s.titre}`.toLowerCase().includes(q)
    })
    .sort((a, b) => a.type.localeCompare(b.type) || (a.numero ?? 1e9) - (b.numero ?? 1e9))

  return (
    <div className="annot-voile" onClick={annuler}>
      <div className="annot-dialogue" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '86vh', overflowY: 'auto' }}>
        <p className="titre">Épingler à cet endroit</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {TYPES.map((t) => (
            <button key={t.cle} className="bouton" onClick={() => creer(t.cle)}>
              Nouvelle {t.singulier}
            </button>
          ))}
        </div>
        {suivis.some((s) => !estFerme(s)) && (
          <>
            <div className="sect" style={{ marginBottom: 0 }}>Ou rattacher un suivi ouvert</div>
            <label className="champ" style={{ marginBottom: 0 }}>
              <input value={filtre} onChange={(e) => setFiltre(e.target.value)} placeholder="Chercher (code ou titre)" />
            </label>
            <div className="liste" style={{ gap: 6 }}>
              {ouverts.slice(0, 40).map((s) => (
                <button key={s.id} className="carte bande-nulle" style={{ padding: '8px 10px' }} onClick={() => rattacher(s)}>
                  <div className="entre">
                    <div style={{ minWidth: 0 }}>
                      <p className="code">
                        {codeSuivi(s)}
                        {s.epingle_libelle ? ` · déjà sur ${s.epingle_libelle} (sera déplacée)` : ''}
                      </p>
                      <p className="titre">{s.titre}</p>
                    </div>
                    <span className={`pastille ${classeStatut(s)}`}>{LIBELLE_STATUT[s.statut] ?? s.statut}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
        <button className="bouton creux" onClick={annuler}>
          Annuler
        </button>
      </div>
    </div>
  )
}
